import React, { useState, useEffect } from 'react';
import { BookOpen, Brain, ChevronRight, ChevronUp, FileText, Layers, Loader2, Menu, Shield, Users, X, Zap } from 'lucide-react';

import MobileMenu from './components/MobileMenu';
import ExternalLink from './components/ExternalLink';
import ScrollProgress from './components/ScrollProgress';
import BackToTop from './components/BackToTop';

const App = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      // Update active section based on scroll position
      const sections = ['hero', 'philosophy', 'ai-augmentation', 'architecture', 'framework', 'community'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const Logo = () => (
    <svg
      className="h-10 w-10"
      viewBox="0 0 250 180"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M 23.5,8.5 83.55,154.5 144,8.5 Z" fill="#11bbff" />
      <path d="M 91,172 124,94 151,94 222.5,172 z" fill="#221199" />
      <path d="M 129,82.5 160,8.5 215.5,8.5 151.5,82.5 z" fill="#bb4455" />
      <path d="M 163.1,87 232.5,8.5 242.3,8.5 242.3,172 z" fill="#cc44aa" />
    </svg>
  );

  var GitlabLogo = ({ className, width = 24, height = 24, ...props }) => (
    <svg
      className={className}
      width={width}
      height={height}
      viewBox="90 90 200 200"
      fill="currentColor"
      {...props}
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="LOGO">
        <path d="M282.83,170.73l-.27-.69-26.14-68.22a6.81,6.81,0,0,0-2.69-3.24,7,7,0,0,0-8,.43,7,7,0,0,0-2.32,3.52l-17.65,54H154.29l-17.65-54A6.86,6.86,0,0,0,134.32,99a7,7,0,0,0-8-.43,6.87,6.87,0,0,0-2.69,3.24L97.44,170l-.26.69a48.54,48.54,0,0,0,16.1,56.1l.09.07.24.17,39.82,29.82,19.7,14.91,12,9.06a8.07,8.07,0,0,0,9.76,0l12-9.06,19.7-14.91,40.06-30,.1-.08A48.56,48.56,0,0,0,282.83,170.73Z"/>
      </g>
    </svg>
  );

  var GithubLogo = ({ className, width = 24, height = 24, ...props }) => (
    <svg
      className={className}
      width={width}
      height={height}
      viewBox="0 0 98 96"
      fill="currentColor"
      {...props}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path fillRule="evenodd" clipRule="evenodd" d="M48.854 0C21.839 0 0 22 0 49.217c0 21.756 13.993 40.172 33.405 46.69 2.427.49 3.316-1.059 3.316-2.362 0-1.141-.08-5.052-.08-9.127-13.59 2.934-16.42-5.867-16.42-5.867-2.184-5.704-5.42-7.17-5.42-7.17-4.448-3.015.324-3.015.324-3.015 4.934.326 7.523 5.052 7.523 5.052 4.367 7.496 11.404 5.378 14.235 4.074.404-3.178 1.699-5.378 3.074-6.6-10.839-1.141-22.243-5.378-22.243-24.283 0-5.378 1.94-9.778 5.014-13.2-.485-1.222-2.184-6.275.486-13.038 0 0 4.125-1.304 13.426 5.052a46.97 46.97 0 0 1 12.214-1.63c4.125 0 8.33.571 12.213 1.63 9.302-6.356 13.427-5.052 13.427-5.052 2.67 6.763.97 11.816.485 13.038 3.155 3.422 5.015 7.822 5.015 13.2 0 18.905-11.404 23.06-22.324 24.283 1.78 1.548 3.316 4.481 3.316 9.126 0 6.6-.08 11.897-.08 13.526 0 1.304.89 2.853 3.316 2.364 19.412-6.52 33.405-24.935 33.405-46.691C97.707 22 75.788 0 48.854 0z" />
    </svg>
  );

  const platformColors = {
    gitlab: {
      text: 'text-orange-400',
      hover: 'hover:text-orange-300',
      border: 'border-orange-500/30',
      bg: 'bg-orange-500/10',
      shadow: 'shadow-orange-500/25'
    },
    github: {
      text: 'text-purple-400',
      hover: 'hover:text-purple-300',
      border: 'border-purple-500/30',
      bg: 'bg-purple-500/10',
      shadow: 'shadow-purple-500/25'
    },
    wiki: {
      text: 'text-emerald-400',
      hover: 'hover:text-emerald-300',
      border: 'border-emerald-500/30',
      bg: 'bg-emerald-500/10',
      shadow: 'shadow-emerald-500/25'
    }
  };

  const navigation = [
    { name: 'Philosophy', href: '#philosophy' },
    { name: 'AI Augmentation', href: '#ai-augmentation' },
    { name: 'Architecture', href: '#architecture' },
    { name: 'Framework', href: '#framework' },
    { name: 'Community', href: '#community' },
    { name: 'Docs', href: 'https://gitlab.com/vigilkeep/intent-based-engineering/-/tree/main/docs', external: true }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-cyan-600 focus:text-white focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400"
      >
        Skip to main content
      </a>
      {/* Navigation */}
      <nav
        className={`fixed w-full z-50 transition-all duration-300 ${
          scrolled ? 'bg-slate-950/95 backdrop-blur-sm shadow-lg shadow-cyan-500/5' : 'bg-transparent'
        }`}
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <Logo />
              <span className="text-xl font-vk font-light tracking-wide translate-y-0.5">VIGIL KEEP</span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navigation.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  target={item.external ? "_blank" : undefined}
                  rel={item.external ? "noopener noreferrer" : undefined}
                  className="text-sm text-slate-300 hover:text-cyan-400 transition-colors duration-200"
                >
                  {item.name}
                </a>
              ))}
            </div>

            {/* Right Side Icons */}
            <div className="flex items-center space-x-4">
              {/* GitLab/GitHub icons (desktop only) */}
              <div className="hidden md:flex items-center space-x-4">
                {/* GitLab and GitHub icon links */}
                <a
                href="https://gitlab.com/vigilkeep/intent-based-engineering"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-orange-400 transition-colors"
                title="GitLab Repository"
              >
                <GitlabLogo className="h-5 w-5" />
              </a>
              <a
                href="https://github.com/vigilkeep/intent-based-engineering/discussions"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-purple-400 transition-colors"
                title="GitHub Discussions"
              >
                <GithubLogo className="h-5 w-5" />
              </a>
              </div>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="md:hidden p-2 rounded-lg hover:bg-slate-800/50 transition-colors"
                aria-label="Open menu"
                aria-expanded={mobileMenuOpen}
              >
                <Menu className="h-6 w-6 text-slate-300" aria-label="Menu icon" />
              </button>
            </div>
          </div>
        </div>
        <ScrollProgress />
      </nav>

      {/* Mobile Menu Component */}
      <MobileMenu 
        navigation={navigation}
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      />

      <main id="main-content">
      {/* Hero Section */}
      <section
        id="hero"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        aria-labelledby="hero-heading"
      >
        {/* Animated background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-fuchsia-500/5 animate-pulse"
             style={{ animationDuration: '8s' }} />

        {/* Castle silhouette subtle background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-full max-w-4xl h-96 bg-gradient-to-t from-slate-800 to-transparent"
               style={{ clipPath: 'polygon(10% 100%, 20% 60%, 25% 60%, 25% 40%, 30% 40%, 30% 20%, 35% 20%, 40% 30%, 45% 30%, 45% 50%, 50% 40%, 55% 50%, 55% 30%, 60% 30%, 65% 20%, 70% 20%, 70% 40%, 75% 40%, 75% 60%, 80% 60%, 90% 100%)' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8 flex justify-center">
            <Logo />
          </div>

          <h1 className="md:text-5xl font-vk font-light mb-6 bg-gradient-to-r from-cyan-400 via-blue-400 to-fuchsia-400 bg-clip-text text-transparent">
            VIGIL KEEP
          </h1>

          <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-8 leading-relaxed font-light">
            A philosophy of active vigilance and enduring preservation—for digital privacy, personal freedom, organizational knowledge, and software architecture.
          </p>

          {/* Key pillars */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12">
            {[
              { icon: Shield, label: 'Preservation' },
              { icon: Brain, label: 'AI Augmentation' },
              { icon: Layers, label: 'Architecture' },
              { icon: BookOpen, label: 'Knowledge' }
            ].map((pillar, idx) => (
              <div key={idx} className="group p-4 rounded-lg bg-slate-800/30 hover:bg-slate-800/50 transition-all duration-300 border border-slate-700/30 hover:border-cyan-500/30">
                <pillar.icon className="h-8 w-8 mx-auto mb-2 text-cyan-400 group-hover:text-cyan-300 transition-colors" />
                <p className="text-sm text-slate-400 group-hover:text-slate-300">{pillar.label}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="#ai-augmentation"
              className="group px-8 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 rounded-lg font-medium transition-all duration-300 shadow-lg shadow-cyan-500/25 flex items-center space-x-2"
            >
              <span>Explore AI Augmentation</span>
              <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </a>
            <a
              href="#framework"
              className="px-8 py-3 border border-slate-600 hover:border-cyan-500 rounded-lg font-medium transition-all duration-300"
            >
              Read the Framework
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <ChevronRight className="h-6 w-6 text-slate-500 rotate-90" aria-hidden="true" />
        </div>
      </section>

      {/* Philosophy Section */}
      <section
        id="philosophy"
        className="py-24 relative"
        aria-labelledby="philosophy-heading"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="philosophy-heading" className="text-4xl md:text-4xl font-vk font-light mb-4">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                The VIGIL KEEP Philosophy
              </span>
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              Active vigilance meets enduring fortification
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="p-8 rounded-xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/30">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <Shield className="h-8 w-8 text-cyan-400" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-xl font-medium mb-3 text-slate-200">Active Vigilance</h3>
                  <p className="text-slate-400 leading-relaxed">
                    Like watchmen on fortress walls, we actively monitor, protect, and preserve what matters—
                    from digital privacy to architectural intent, from personal freedom to organizational knowledge.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8 rounded-xl bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700/30">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 text-fuchsia-400" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 21h18M5 21V7l8-4v18M13 21V7M9 9h.01M9 12h.01M9 15h.01" />
                    </svg>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-medium mb-3 text-slate-200">Enduring Preservation</h3>
                  <p className="text-slate-400 leading-relaxed">
                    Like castle walls built to withstand sieges, we create systems designed for longevity—
                    preserving context, maintaining comprehensibility, and ensuring knowledge endures across time.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 p-8 rounded-xl bg-gradient-to-r from-slate-800/30 to-slate-900/30 border border-slate-700/30 max-w-5xl mx-auto">
            <p className="text-lg text-slate-300 leading-relaxed italic text-center">
              "A keep is both fortress and refuge—standing strong against threats while providing safe haven
              for what must be preserved. So too must our systems protect and preserve what matters most."
            </p>
          </div>
        </div>
      </section>

      {/* AI Augmentation Section - PROMINENT */}
      <section
        id="ai-augmentation"
        className="py-24 relative bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900"
        aria-labelledby="ai-augmentation-heading"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 via-transparent to-fuchsia-500/5" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/10 to-fuchsia-500/10 border border-cyan-500/20 mb-6">
              <Zap className="h-4 w-4 text-cyan-400" aria-hidden="true" />
              <span className="text-sm text-cyan-400 font-medium">Revolutionary Approach</span>
            </div>

            <h2 id="ai-augmentation-heading" className="text-4xl md:text-5xl font-vk font-light mb-6">
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-fuchsia-400 bg-clip-text text-transparent">
                AI Augmentation Framework
              </span>
            </h2>

            <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Harness AI's potential while preserving engineering excellence and learning trajectories
            </p>
          </div>

          {/* Value Propositions Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {[
              {
                icon: Brain,
                title: 'For Engineers',
                description: 'Accelerate development without sacrificing learning. AI as instrument, not crutch.',
                gradient: 'from-cyan-500/10 to-cyan-500/5'
              },
              {
                icon: Users,
                title: 'For Organizations',
                description: 'Measurable productivity gains while maintaining code quality and architectural integrity.',
                gradient: 'from-blue-500/10 to-blue-500/5'
              },
              {
                icon: Zap,
                title: 'For the Future',
                description: 'Sustainable AI adoption that builds expertise rather than creating dependency.',
                gradient: 'from-fuchsia-500/10 to-fuchsia-500/5'
              }
            ].map((item, idx) => (
              <div key={idx} className={`p-8 rounded-xl bg-gradient-to-br ${item.gradient} border border-slate-700/30 hover:border-cyan-500/30 transition-all duration-300`}>
                <item.icon className="h-10 w-10 text-cyan-400 mb-4" aria-hidden="true" />
                <h3 className="text-xl font-medium mb-3 text-slate-200">{item.title}</h3>
                <p className="text-slate-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          {/* Key Features */}
          <div className="bg-slate-800/30 rounded-2xl p-8 md:p-12 border border-slate-700/30">
            <h3 className="text-3xl font-vk font-light mb-8 text-center">Framework Pillars</h3>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  title: 'Intent-First Collaboration',
                  description: 'AI understands your architectural intent through layered context, producing solutions aligned with your system philosophy.',
                  icon: '🎯'
                },
                {
                  title: 'Maturity-Based Guidance',
                  description: 'From Foundation to Distinguished Engineer—guidance scales with your expertise level, supporting growth at every stage.',
                  icon: '📈'
                },
                {
                  title: 'Layer-Aware Generation',
                  description: 'AI respects Capability → Constraints → Construction boundaries, maintaining architectural integrity automatically.',
                  icon: '🏗️'
                },
                {
                  title: 'Quality Assurance',
                  description: 'Detect "AI slop" through measurable criteria, automated validation, and architectural drift monitoring.',
                  icon: '✨'
                },
                {
                  title: 'Learning Preservation',
                  description: 'Deliberate practice patterns ensure AI augmentation enhances rather than replaces skill development.',
                  icon: '🎓'
                },
                {
                  title: 'Organizational Integration',
                  description: 'Adoption roadmaps, metrics, and change management strategies for successful team-wide implementation.',
                  icon: '🏢'
                }
              ].map((feature, idx) => (
                <div key={idx} className="flex space-x-4">
                  <div className="text-3xl flex-shrink-0" aria-hidden="true">{feature.icon}</div>
                  <div>
                    <h4 className="text-lg font-medium mb-2 text-slate-200">{feature.title}</h4>
                    <p className="text-slate-400 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 flex justify-center">
              <ExternalLink
                href="https://gitlab.com/vigilkeep/intent-based-engineering/-/tree/main/docs/ai-augmentation"
                className="inline-flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 rounded-lg font-medium transition-all duration-300 shadow-lg shadow-cyan-500/25"
              >
                <BookOpen className="h-5 w-5" aria-hidden="true" />
                <span>Read Full Framework (Parts 1-8)</span>
              </ExternalLink>
            </div>
          </div>

          {/* Impact Statement */}
          <div className="mt-12 text-center">
            <p className="text-2xl font-light text-slate-300 max-w-4xl mx-auto leading-relaxed">
              "AI augmentation isn't about replacing engineers—it's about amplifying their capabilities
              while preserving the craft that makes great software possible."
            </p>
          </div>
        </div>
      </section>

      {/* Layered Architecture Section */}
      <section
        id="architecture"
        className="py-24 relative"
        aria-labelledby="architecture-heading"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="architecture-heading" className="text-4xl md:text-4xl font-vk font-light mb-4">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Layered Architecture
              </span>
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              Capability → Constraints → Construction
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            {/* Architecture Layers */}
            <div className="space-y-6">
              {[
                {
                  layer: 'Capability',
                  color: 'cyan',
                  description: 'Core mechanisms and functionality—what the system can do',
                  examples: 'Data operations, business logic, essential features',
                  gradient: 'from-cyan-500/20 to-cyan-500/5'
                },
                {
                  layer: 'Constraints',
                  color: 'blue',
                  description: 'Business rules and operational policies—what the system should do',
                  examples: 'Validation rules, access policies, environment-specific logic',
                  gradient: 'from-blue-500/20 to-blue-500/5'
                },
                {
                  layer: 'Construction',
                  color: 'fuchsia',
                  description: 'Deployment composition and infrastructure—how the system is built',
                  examples: 'Rate limiting, monitoring, deployment configs, infrastructure',
                  gradient: 'from-fuchsia-500/20 to-fuchsia-500/5'
                }
              ].map((item, idx) => (
                <div key={idx} className={`p-8 rounded-xl bg-gradient-to-r ${item.gradient} border border-slate-700/30 hover:border-${item.color}-500/30 transition-all duration-300`}>
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-2xl font-medium text-slate-200">{item.layer}</h3>
                    <Layers className={`h-6 w-6 text-${item.color}-400`} />
                  </div>
                  <p className="text-lg text-slate-300 mb-3">{item.description}</p>
                  <p className="text-sm text-slate-500">Examples: {item.examples}</p>
                </div>
              ))}
            </div>

            {/* Benefits */}
            <div className="mt-12 grid md:grid-cols-2 gap-6">
              {[
                'Better separation of concerns',
                'More testable components',
                'Flexible constraint composition',
                'Reusable core logic',
                'Clear architectural boundaries',
                'Performance optimized design'
              ].map((benefit, idx) => (
                <div key={idx} className="flex items-start space-x-3 p-4 rounded-lg bg-slate-800/30">
                  <ChevronRight className="h-5 w-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Framework Section */}
      <section
        id="framework"
        className="py-24 relative bg-gradient-to-br from-slate-900 to-slate-950"
        aria-labelledby="framework-heading"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="framework-heading" className="text-4xl md:text-4xl font-vk font-light mb-4">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Intent-Based Engineering
              </span>
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              A framework for building self-describing software systems
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-8">
            {[
              {
                title: 'Preserve Intent',
                description: 'Capture the why behind technical decisions through systematic documentation and Architecture Decision Records.',
                icon: FileText
              },
              {
                title: 'Cross-Domain Solutions',
                description: 'Find better solutions in the intersection of business and technical domains through collaborative design.',
                icon: Users
              },
              {
                title: 'Scale Gracefully',
                description: 'From solo development to enterprise—adoption paths that grow with your organization.',
                icon: Layers
              },
              {
                title: 'Prevent Archaeological Decay',
                description: 'Systems that remain comprehensible over time, even to their original creators.',
                icon: Shield
              }
            ].map((item, idx) => (
              <div key={idx} className="flex items-start space-x-6 p-6 rounded-xl bg-slate-800/30 border border-slate-700/30 hover:border-cyan-500/30 transition-all duration-300">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                    <item.icon className="h-6 w-6 text-cyan-400" aria-hidden="true" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-medium mb-2 text-slate-200">{item.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <ExternalLink
              href="https://gitlab.com/vigilkeep/intent-based-engineering/-/blob/main/docs/framework/intent-based-engineering.md"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-8 py-3 bg-slate-800 hover:bg-slate-700 rounded-lg font-medium transition-all duration-300 border border-slate-700"
            >
              <BookOpen className="h-5 w-5" />
              <span>Read Complete Documentation</span>
            </ExternalLink>
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section
        id="community"
        className="py-24 relative"
        aria-labelledby="community-heading"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="community-heading" className="text-4xl md:text-4xl font-vk font-light mb-4">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Join the Community
              </span>
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto mb-6">
              Collaborate, contribute, and help evolve the framework
            </p>

            {/* Dual Platform Explanation */}
            <div className="max-w-4xl mx-auto p-6 rounded-xl bg-slate-800/30 border border-slate-700/30">
              <div className="flex items-start space-x-3 mb-3">
                <div className="flex items-center space-x-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-orange-400"></span>
                  <span className="text-xs text-orange-400 font-medium">GitLab</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-purple-400"></span>
                  <span className="text-xs text-purple-400 font-medium">GitHub</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-400"></span>
                  <span className="text-xs text-emerald-400 font-medium">Wiki</span>
                </div>
              </div>
              <p className="text-slate-300 leading-relaxed">
                <span className="font-medium text-cyan-400">Dual-Platform Architecture:</span> The framework uses{' '}
                <span className="text-orange-400">GitLab</span> for documentation and formal contributions, while{' '}
                <span className="text-purple-400">GitHub Discussions</span> provides a community forum for 
                open exploration, Q&A, and collaborative learning. The{' '}
                <span className="text-emerald-400">GitLab Wiki</span> serves as Part 8—living documentation 
                that captures real-world validation and evolving patterns.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              {
                icon: GitlabLogo,
                iconclass: 'h-10 w-10 text-orange-400 mb-4',
                title: 'GitLab Repository',
                class: 'p-8 rounded-xl bg-slate-800/30 border border-orange-500/30 hover:border-orange-500/50 hover:bg-gradient-to-br hover:from-orange-500/5 hover:to-transparent transition-all duration-300 flex flex-col',
                description: 'Source of truth for framework documentation, templates, and formal contributions',
                link: 'https://gitlab.com/vigilkeep/intent-based-engineering',
                linkclass: 'inline-flex items-center space-x-2 text-orange-400 hover:text-orange-300 font-medium transition-colors',
                cta: 'View Repository'
              },
              {
                icon: GithubLogo,
                iconclass: 'h-10 w-10 text-purple-400 mb-4',
                title: 'GitHub Discussions',
                class: 'p-8 rounded-xl bg-slate-800/30 border border-purple-500/30 hover:border-purple-500/50 hover:bg-gradient-to-br hover:from-purple-500/5 hover:to-transparent transition-all duration-300 flex flex-col',
                description: 'Community forum for Q&A, implementation experiences, and collaborative exploration',
                link: 'https://github.com/vigilkeep/intent-based-engineering/discussions',
                linkclass: 'inline-flex items-center space-x-2 text-purple-400 hover:text-purple-300 font-medium transition-colors',
                cta: 'Join Discussion'
              },
              {
                icon: BookOpen,
                iconclass: 'h-10 w-10 text-emerald-400 mb-4',
                title: 'Living Documentation',
                class: 'p-8 rounded-xl bg-slate-800/30 border border-emerald-500/30 hover:border-emerald-500/50 hover:bg-gradient-to-br hover:from-emerald-500/5 hover:to-transparent transition-all duration-300 flex flex-col',
                description: 'Part 8: Implementation cases, validated patterns, and research validation on GitLab Wiki',
                link: 'https://gitlab.com/vigilkeep/intent-based-engineering/-/wikis/home',
                linkclass: 'inline-flex items-center space-x-2 text-emerald-400 hover:text-emerald-300 font-medium transition-colors',
                cta: 'Explore Wiki'
              }
            ].map((item, idx) => (
              <div key={idx} className={item.class}>
                <item.icon className={item.iconclass} aria-hidden="true" />
                <h3 className="text-xl font-medium mb-3 text-slate-200">{item.title}</h3>
                <p className="text-slate-400 leading-relaxed mb-6 flex-grow">{item.description}</p>
                <ExternalLink
                  href={item.link}
                  className={item.linkclass}
                >
                  <span>{item.cta}</span>
                  <ChevronRight className="h-4 w-4" />
                </ExternalLink>
              </div>
            ))}
          </div>

          {/* Research Status */}
          <div className="mt-16 max-w-3xl mx-auto p-8 rounded-xl bg-gradient-to-r from-amber-500/5 to-orange-500/5 border border-amber-500/20">
            <p className="text-center text-slate-300 leading-relaxed">
              <span className="font-medium text-amber-400">Framework Status:</span> This represents research-in-progress requiring empirical validation.
              Your implementation experience—both successes and failures—is essential for framework evolution.
            </p>
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex items-center space-x-3">
              <Logo />
              <span className="font-vk font-light text-slate-400 translate-y-0.5">VIGIL KEEP</span>
            </div>

            <div className="flex items-center space-x-6 text-sm text-slate-400">
              <ExternalLink
                href="https://gitlab.com/vigilkeep/intent-based-engineering/-/blob/main/LICENSE-APACHE-2.0" 
                className="hover:text-cyan-400 transition-colors"
              >
                License
              </ExternalLink>
              <ExternalLink
                href="https://gitlab.com/vigilkeep/intent-based-engineering/-/blob/main/CONTRIBUTING.md" 
                className="hover:text-orange-400 transition-colors"
              >
                Contributing
              </ExternalLink>
              <ExternalLink
                href="https://gitlab.com/vigilkeep/intent-based-engineering" 
                className="hover:text-orange-400 transition-colors"
              >
                GitLab
              </ExternalLink>
              <ExternalLink
                href="https://github.com/vigilkeep/intent-based-engineering/discussions" 
                className="hover:text-purple-400 transition-colors"
              >
                Discussions
              </ExternalLink>
            </div>

            <p className="text-sm text-slate-500">
              © 2025 VIGIL KEEP. Apache License 2.0
            </p>
          </div>
        </div>
      </footer>
      <BackToTop />
    </div>
  );
};

export default App;
